import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import {
  casePageUrl,
  demoPageUrl,
  extractionPageUrl,
  getPlan,
  useCase,
  type ClosureCheck,
  type DemoRunResult,
  type EdgeMoveInput,
} from "@/api";
import { Button } from "@/components/Button";
import { OutlinePicker } from "@/components/OutlinePicker";
import { OutlineReview } from "@/components/OutlineReview";
import { Panel } from "@/components/Panel";
import { PartsList } from "@/components/PartsList";
import { RunSchema } from "@/components/RunSchema";
import { StateMessage } from "@/components/StateMessage";
import { StatusBadge } from "@/components/StatusBadge";
import { formatMm } from "@/lib/format";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { useDemoRun } from "@/store/demoRun";
import { useSession } from "@/store/session";

import styles from "./SchemaScreen.module.css";
import { TakeOver } from "./TakeOver";

/**
 * Why the system drew a shape nobody asked for, in words — keyed by the
 * backend's own `AutoReason`.
 *
 * `dominant` covers two arguments over there (the runner-up gapes at least
 * twice as far, or the first closes and the second does not), so the sentence
 * stays at the level both share: the drawing settled it, not a preference of
 * ours. Neither word ever appears on screen — a reader is owed the reason, not
 * the enum.
 *
 * `label_agreement` is a different argument and gets its own sentence: the
 * shapes were equally close, and what separated them was the names the reading
 * gave the sides — „Vorne" facing „Hinten", „Links" at a corner from both.
 * Saying "eindeutig nach Abgleich mit der Zeichnung" there would claim the
 * geometry decided when it could not.
 */
const AUTO_REASONS: Record<string, string> = {
  single_candidate: "nur eine mögliche Form aus den gelesenen Teilzügen",
  dominant: "eindeutig nach Abgleich mit der Zeichnung",
  label_agreement: "eindeutig nach den gelesenen Seitenbezeichnungen",
  // Not "die beste von mehreren": nothing was ranked. The model described the
  // line around the building and the geometry was calculated from it, so the
  // sentence has to say where the form came from and not how well it scored.
  contour: "aus der beschriebenen Fassadenkontur gezeichnet",
};

/**
 * The escalation, in the design's own words (spec 2026-08-12 §3).
 *
 * It is a heading and not a hint: where two readings are equally plausible,
 * choosing one would be guessing, and E-16 says the machine does not guess. So
 * the choice is open on arrival, nothing is drawn behind it, and the sentence
 * says whose decision it now is.
 */
const AMBIGUOUS_HEADING = "Zwei plausible Formen — deine Entscheidung";

/**
 * Why the result on screen is not the current one — for every control that
 * would otherwise act on it.
 *
 * Same wording as `SUPERSEDED_NOTE` in `features/demo/DemoScreen.tsx`, not
 * imported from there: `import/no-restricted-paths` (R-W1) forbids one feature
 * reaching into another. A failed re-run leaves the previous answer standing
 * (the store keeps it on purpose), and with it a picker whose ids the backend
 * may no longer know and a confirm button that would write "Bestätigt von …"
 * under a red error card.
 */
const SUPERSEDED_NOTE =
  "Abgebrochener Lauf — diese Formen stammen aus dem Lauf davor. Erst ein neuer Lauf bringt " +
  "eine Form, die sich wählen und bestätigen lässt.";

function ClosureReadout({
  closure,
  message = true,
}: {
  closure: ClosureCheck;
  /**
   * Whether the verdict is spelled out under the sums.
   *
   * The fixture path is the only place that owns this sentence outright — its
   * drawing has nothing else to say it. In the real run `PartsList` already
   * prints the same sentence under the same Teilzug a section further down,
   * and one finding printed twice on one page reads as two (fix round,
   * 12.08.2026). It stays with the parts list, where the elements it is about
   * are: the sums here carry their own verdict as a word in the badge.
   */
  message?: boolean;
}) {
  return (
    <div className={styles.closure}>
      <dl className={styles.sums}>
        <div>
          <dt className="label">Σ horizontal</dt>
          <dd className={`num ${styles.sum}`} data-zero={closure.sumHorizontalMm === 0}>
            {closure.evaluable ? formatMm(closure.sumHorizontalMm) : "—"}
          </dd>
        </div>
        <div>
          <dt className="label">Σ vertikal</dt>
          <dd className={`num ${styles.sum}`} data-zero={closure.sumVerticalMm === 0}>
            {closure.evaluable ? formatMm(closure.sumVerticalMm) : "—"}
          </dd>
        </div>
      </dl>
      {message ? <p className={styles.closureText}>{closure.message}</p> : null}
    </div>
  );
}

/**
 * Who the shape on screen came from — three answers, not two.
 *
 * A correction is not a choice. `applyEdits` sends the drawn shape's id
 * along, so the answer comes back with `chosenBy: "user"` even where nobody
 * ever picked anything: the system drew the shape and a person moved one wall
 * of it. Reading `chosenBy` alone made that line claim "Vom Bearbeiter
 * gewählt", which is a decision the person never made (found in the browser,
 * fix round 12.08.2026).
 *
 * Where the shape originally came from is still on the wire and needs no
 * history of our own: `autoOutline` names the unambiguous candidate whether
 * or not the request used it, so it equals the drawn id exactly when the
 * system is the one that drew it.
 */
function shapeOrigin(
  result: DemoRunResult,
  drawnId: string,
): { word: string; note: string | null } {
  const fromSystem = result.autoOutline !== null && result.autoOutline === drawnId;
  // Both fields describe the same event — a wall somebody moved. A correction
  // reined in at the 1 mm boundary is still a correction, so it counts here
  // the same way `Adjustments` in `OutlineReview` counts it.
  const adjusted = result.adjustments.length > 0 || result.editsClamped.length > 0;

  if (adjusted) {
    return {
      word: "Vom Bearbeiter angepasst",
      note: fromSystem ? "ursprünglich automatisch erkannt" : null,
    };
  }
  if (result.chosenBy === "system") {
    return {
      word: "Automatisch erkannt",
      // The raw word where the vocabulary ever grows past this table: an
      // unknown reason printed as it arrived is still a reason, a blank space
      // is not.
      note: AUTO_REASONS[result.autoReason ?? ""] ?? result.autoReason,
    };
  }
  return { word: "Vom Bearbeiter gewählt", note: null };
}

/**
 * The run the three work screens share, at its last step (design spec
 * 2026-08-12 §3).
 *
 * Three states, and which one is on screen is the backend's answer, not a
 * preference here:
 *
 * 1. **A shape arrives drawn.** The backend found the reading unambiguous and
 *    planned it (`chosenBy: "system"`). It is drawn, draggable and priced —
 *    and labelled "unbestätigt" until a person says otherwise. A-014 is not
 *    weakened by drawing early; it is weakened by *claiming* early, and
 *    nothing here claims.
 * 2. **Two readings compete.** Then nothing is drawn at all and the choice is
 *    the screen (E-16). The order in the picker is closure quality, not a
 *    recommendation.
 * 3. **No connected arrangement exists.** Then the honest answer is that there
 *    is no shape — including for a sheet the run rejected outright (B-2).
 */
function RealSchema({ result }: { result: DemoRunResult }) {
  const navigate = useNavigate();
  const edits = useDemoRun((state) => state.edits);
  const status = useDemoRun((state) => state.status);
  const error = useDemoRun((state) => state.error);
  const applyEdits = useDemoRun((state) => state.applyEdits);
  const chooseOutline = useDemoRun((state) => state.chooseOutline);
  // A real Vorgang (task 15) reopens under its own route, which is what
  // writes every further gesture back to the server; the register demo's
  // `caseId` stays empty and keeps the stateless `/planung` it always had.
  const vorgangId = useDemoRun((state) => state.caseId);
  const planningPath = vorgangId ? `/vorgang/${vorgangId}/planung` : "/planung";
  // A real Vorgang's `document.id` is the case id, not a document one
  // (`useCasePlan`'s own answer) — `demoPageUrl` would ask the demo process
  // for a document it never heard of, so the drawing has to come from the
  // case-scoped route instead, keyed off the Vorgang's own first document.
  const caseDetail = useCase(vorgangId || null);
  const reviewer = useSession((state) => state.reviewer);
  const [confirmedOutlineId, setConfirmedOutlineId] = useState<string | null>(null);
  // A worker's reading (design 2026-09-23 §3.2): taken over by a click here,
  // and its drawing is the page the model saw, turned — not the original file.
  const fromExtraction = useDemoRun((state) => state.fromExtraction);
  // Local state alone traps a person who *leaves* the schema after taking a
  // reading over and comes back to it later — a fresh mount starts
  // `takenOver` at `false` again while `fromExtraction` stays `true` in the
  // store, which is still true for this same, now-standing Vorgang. `TakeOver`
  // then answers a 409 whose advice ("in einen neuen Vorgang einlesen") is
  // wrong here, and the take-over bar reappears over a Stand that already
  // exists. `caseDetail` (already loaded above) is the server truth this
  // needs: a Vorgang with `latestVersionNo > 0` has a Stand, whoever wrote it.
  // Local `takenOver` still matters for the one render between a successful
  // click and `caseDetail`'s own refetch landing.
  const [takenOver, setTakenOver] = useState(false);
  const hasStand = (caseDetail.data?.latestVersionNo ?? 0) > 0;
  // A-014/PF-8: a reading nobody has turned into a Stand must not have a way
  // to Planung — neither the header's quiet link nor `OutlineReview`'s own
  // continue button once a shape is confirmed there.
  const awaitingTakeOver = fromExtraction && !takenOver && !hasStand;

  const busy = status === "running";
  const superseded = status === "error" ? SUPERSEDED_NOTE : null;
  // Dragging a wall and picking another reading re-run the case through
  // `/demo/run`, which knows the demo register and not a stored Vorgang —
  // on a Vorgang both answered 404 (plan 4 §4, „Kaputte Wege schließen").
  // There the shape is corrected in the editor instead.
  const onVorgang = vorgangId !== "";
  // No fallback: `chosen` is null until the backend reports a shape somebody —
  // a person or, since 12.08.2026, the system itself — actually settled on.
  // Nothing was read on this path. The backend says so (`drawnByHand`): on a
  // stored Vorgang from Stand 1's origin, not from the page count — since
  // 13.10.2026 uploads count their pages, and a Vorgang with a sheet can
  // carry an outline drawn by hand.
  const byHand = result.drawnByHand;
  const chosen = result.outlines.find((shape) => shape.chosen) ?? null;
  const connected = result.outlines.some((shape) => shape.connected);
  const confirmedBy = confirmedOutlineId === null ? null : reviewer;
  /** Null exactly while no shape is drawn — the escalation's own condition. */
  const origin = chosen === null ? null : shapeOrigin(result, chosen.id);

  /**
   * Both store calls below are started and not awaited, and both take their
   * own rejection.
   *
   * The store records a failure in `status`/`error` (shown above the panes)
   * and rethrows it for callers that await — the Eingang's `DropArea` does, to
   * stay on its own page. Nothing here awaits, so the rejection is caught rather than
   * left to surface as an unhandled one in the console.
   */
  function choose(outlineId: string) {
    // A confirmation belongs to the shape it was given for. Another shape is
    // another shape, and the store drops the corrections with it.
    setConfirmedOutlineId(null);
    void chooseOutline(outlineId).catch(() => {});
  }

  function adjust(next: EdgeMoveInput[]) {
    // Same rule one step smaller: a corrected wall is not the form somebody
    // signed off, so the signature falls away with the correction (A-014).
    setConfirmedOutlineId(null);
    void applyEdits(next).catch(() => {});
  }

  return (
    <div className={styles.screen}>
      <header className={styles.head}>
        {/* The kicker over the screen's title carries its one red mesh marker,
            as a screen head's does (Task 11, UX-Gutachten 1, B-11). */}
        <p className={`label ${styles.kicker}`}>
          <span className="marke marke-titel" aria-hidden="true" />
          {byHand ? "Schema · von Hand gezeichnet" : `Schema · Fall ${result.caseId}`}
        </p>
        <h1 className={styles.title}>Rostlauf-Schema</h1>
        {/* Two sentences for two origins. „aus den gelesenen Maßen" is a claim
            about a reading, and on a hand-drawn outline nothing was read — the
            figures were typed, and saying otherwise would put a model between
            a person and their own number. */}
        <p className={styles.lede}>
          {byHand
            ? "Maßstabsgetreu aus den Maßen, die du gesetzt hast. Stimmt die Form, stimmen die Maße — ein falsch getipptes Maß bricht die Form sichtbar auf. Wände ohne Maß sind gestrichelt; ihre Länge folgt aus der Schließung."
            : onVorgang
              ? "Maßstabsgetreu aus den gelesenen Maßen gezeichnet. Stimmt die Form, stimmen die Maße — ein falsch gelesenes Maß bricht die Form sichtbar auf. Stimmt sie nicht, zeichne den Umriss selbst."
              : "Maßstabsgetreu aus den gelesenen Maßen gezeichnet. Stimmt die Form, stimmen die Maße — ein falsch gelesenes Maß bricht die Form sichtbar auf. Eine Wand lässt sich anfassen und verschieben; gerechnet wird die Korrektur im Backend, nicht im Bild."}
        </p>
        <p className={styles.wayOn}>
          {byHand ? (
            <Link className={styles.backToDraft} to="/umriss">
              Umriss weiter bearbeiten
            </Link>
          ) : null}
          {/* Forwards as well as back. Planung is a tab of a Vorgang
              (`app/caseTabs.ts`), but the way on belongs where the step ends —
              nobody looks up at the tabs for the next thing to do. A reading
              nobody has taken over yet (A-014/PF-8) gets no way to Planung at
              all — not even a disabled link, which a keyboard or
              screen-reader user could still activate; a plain span with the
              same hint text says the same thing without offering an action. */}
          {awaitingTakeOver ? (
            <span className={styles.wayOnDisabled} title="Erst übernehmen">
              Weiter zur Planung <span className={styles.wayOnHint}>— Erst übernehmen</span>
            </span>
          ) : (
            <Link className={styles.backToDraft} to={planningPath}>
              Weiter zur Planung
            </Link>
          )}
        </p>
      </header>

      {/* Not `hasStand`: a reading whose Vorgang already carries a Stand
          needs no bar at all — there is nothing left to take over, and
          showing it anyway would offer a click that only 409s. */}
      {fromExtraction && vorgangId && !hasStand ? (
        <TakeOver
          caseId={vorgangId}
          documentJson={result.documentJson}
          onDone={() => {
            setTakenOver(true);
            navigate(planningPath);
          }}
        />
      ) : null}

      {status === "error" ? (
        <StateMessage tone="conflict" title="Der Lauf ist abgebrochen.">
          {error}
        </StateMessage>
      ) : null}

      {!connected ? (
        <StateMessage title="Keine Form aus den gelesenen Teilzügen">
          {result.usable
            ? "Aus den gelesenen Teilzügen ließ sich keine zusammenhängende Anordnung bilden. Ohne Form gibt es nichts, das sich mit der Skizze vergleichen und bestätigen ließe."
            : "Der Lauf hat dieses Blatt abgewiesen — es gibt keine Maße, aus denen sich eine Form bilden ließe. Der Grund steht im vorigen Schritt."}{" "}
          Der manuelle Weg bleibt offen.
        </StateMessage>
      ) : origin === null && onVorgang ? (
        <section className={styles.escalation}>
          <h2 className={styles.escalationTitle}>{AMBIGUOUS_HEADING}</h2>
          <p className={styles.escalationText}>
            Keine der gelesenen Anordnungen ist eindeutig genug, um sie automatisch zu zeichnen.
            Das System rät nicht (E-16) — zeichne den Umriss für diesen Vorgang selbst.
          </p>
          <p>
            <Link className="inline-link" to={`/umriss?case=${encodeURIComponent(vorgangId)}`}>
              Umriss selbst zeichnen
            </Link>
          </p>
        </section>
      ) : origin === null ? (
        <section className={styles.escalation}>
          <h2 className={styles.escalationTitle}>{AMBIGUOUS_HEADING}</h2>
          <p className={styles.escalationText}>
            Keine der gelesenen Anordnungen ist eindeutig genug, um sie automatisch zu zeichnen.
            Das System rät nicht: zwei plausible Formen sind eine Entscheidung und keine
            Rechenaufgabe (E-16). Die Reihenfolge unten ist Schließungsgüte, keine Empfehlung —
            gewählt wird erst gerechnet und gezeichnet.
          </p>
          <OutlinePicker
            outlines={result.outlines}
            onChoose={choose}
            busy={busy}
            unavailable={superseded}
          />
        </section>
      ) : (
        <>
          <p className={styles.status}>
            <StatusBadge status={confirmedBy === null ? "review" : "confirmed"}>
              {confirmedBy === null ? "Unbestätigt" : "Bestätigt"}
            </StatusBadge>
            <strong className={styles.statusHead}>
              {`${origin.word} — ${confirmedBy === null ? "unbestätigt" : "bestätigt"}`}
            </strong>
            {origin.note !== null ? (
              <span className={styles.statusWhy}>{origin.note}</span>
            ) : null}
          </p>

          <OutlineReview
            outlines={result.outlines}
            findings={result.outlineFindings}
            // Empty where no sheet was ever taken in — a hand-drawn outline
            // (design 2026-08-15). `OutlineReview` then draws no Original pane
            // rather than reporting one that failed to load. A worker's
            // reading (design 2026-09-23 §3.2) draws the page the model saw,
            // turned — not the original file, which this screen never fetches.
            drawingUrl={
              fromExtraction && vorgangId
                ? extractionPageUrl(vorgangId, 1)
                : vorgangId && caseDetail.data?.documents[0]
                  ? casePageUrl(vorgangId, caseDetail.data.documents[0].id, 1)
                  : !vorgangId && result.document.pageCount > 0
                    ? demoPageUrl(result.document.id, 1)
                    : ""
            }
            // While the Vorgang's documents are still being looked up the url
            // above is empty for want of an answer, not because there is no
            // sheet — and „kein Blatt dahinter" is a claim, not a placeholder.
            // Not on the extraction path: its url above needs no lookup.
            drawingPending={Boolean(vorgangId) && !fromExtraction && caseDetail.isPending}
            onChoose={choose}
            onConfirm={setConfirmedOutlineId}
            onRevoke={() => setConfirmedOutlineId(null)}
            confirmedBy={confirmedBy}
            // The confirmed row leads on. The header's quiet link stays, but
            // the moment of completion is down here, and it must not end in
            // „zurücknehmen" alone (seen in use, 16.08.2026). Withheld while a
            // reading is waiting on `TakeOver` (A-014/PF-8) — confirming the
            // shape here is not the same as making it Stand 1, and this
            // button must not be the second way past that gate.
            onContinue={awaitingTakeOver ? undefined : () => navigate(planningPath)}
            continueLabel="Weiter zur Planung"
            busy={busy}
            superseded={superseded}
            // The alternatives are folded away below instead — one picker on
            // the page, and the drawn shape leads.
            picker={false}
            edits={edits}
            onEdits={onVorgang ? undefined : adjust}
            adjustments={result.adjustments}
            editsClamped={result.editsClamped}
            // Nothing was read on this path; the legend must not say it was.
            figuresAre={byHand ? "set" : "read"}
          />

          {/* Collapsed, not hidden: a second reading exists and stays one
              click away, but it is not the question on this screen any more —
              the drawn shape is (design 12.08.2026 §3). */}
          {onVorgang ? null : (
            <details className={styles.alternatives}>
              <summary className={styles.alternativesHead}>Andere Lesarten</summary>
              <OutlinePicker
                outlines={result.outlines}
                onChoose={choose}
                busy={busy}
                unavailable={superseded}
              />
            </details>
          )}

          {result.runs.length > 0 ? (
            <Panel
              title="Schließung je Teilzug"
              hint="geprüft wird nur, was vollständig bemaßt ist (E-15)"
              flush
            >
              <ul className={styles.runClosures}>
                {result.runs.map((run) => (
                  <li key={run.id} className={styles.runClosure}>
                    <p className={styles.runLabel}>
                      <span>{run.label}</span>
                      <StatusBadge status={run.closure.status}>{closureWord(run.closure)}</StatusBadge>
                    </p>
                    {/* The sentence stays with the parts list below, which
                        prints it under the same Teilzug (see `message`). */}
                    <ClosureReadout closure={run.closure} message={false} />
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}

          {/* No parts list on a hand-drawn outline. The design says so in as
              many words (2026-08-15 §5: kein Gitterrost, keine Teilzüge, keine
              Konsolen, keine Stückliste auf dieser Fläche) — and the empty
              one printed „Aus den gelesenen Maßen ließ sich kein Rostlauf
              bilden", which names a reading that never happened. */}
          {byHand ? null : (
          <section className={styles.partsSection}>
            <div className={styles.partsHead}>
              <h2 className={styles.sectionTitle}>Stückliste</h2>
              <dl className={styles.grandTotals}>
                <div>
                  <dt className="label">Elemente</dt>
                  <dd className="num">{result.totals.elements}</dd>
                </div>
                <div>
                  <dt className="label">Konsolen</dt>
                  <dd className="num">
                    {/* An upper bound is never printed as a bare number: it is
                        the figure somebody would order against. */}
                    {result.totals.consolesExact ? null : (
                      <span className={styles.bound}>höchstens </span>
                    )}
                    {result.totals.consoles}
                  </dd>
                </div>
                <div>
                  <dt className="label">Teilzüge</dt>
                  <dd className="num">{result.runs.length}</dd>
                </div>
              </dl>
            </div>

            {result.totals.consolesExact ? null : (
              <p className={styles.boundNote}>{result.totals.consolesNote}</p>
            )}
            <PartsList runs={result.runs} sections={result.partsList} grating={result.grating} />
            {/* The register demo's sentence. A Vorgang's Stand is stored and
                priced on its own calculation — there it was simply false
                (Welle 0, 18.10.2026). */}
            {onVorgang ? null : (
              <p className={styles.foot}>
                Kein Preis. Die Preislisten liegen noch nicht vor — eine erfundene Zahl wäre hier
                schlimmer als keine. Gespeichert ist nichts, auch die Bestätigung der Form nicht.
              </p>
            )}
          </section>)}
        </>
      )}
    </div>
  );
}

export function SchemaScreen() {
  useDocumentTitle("Schema");

  // The demo run is the whole point of the three-step flow (design spec
  // 2026-08-12 §3): once one exists, it is what this screen shows. The fixture
  // path below is what E1's own endpoints answer with once they exist —
  // untouched, and still reachable whenever no run has started.
  const result = useDemoRun((state) => state.result);
  // The fixture is example data. In the production build K60 works on, an
  // address typed with no run in hand leads to the start page instead
  // (Welle 0, S3, 18.10.2026); development keeps the sample, like `/_muster`.
  const sample = import.meta.env.DEV;
  const plan = useQuery({ queryKey: ["plan"], queryFn: getPlan, enabled: result === null && sample });

  if (result) {
    return <RealSchema result={result} />;
  }

  if (!sample) {
    return <Navigate to="/" replace />;
  }

  if (plan.isPending) {
    return <StateMessage title="Schema wird aufgebaut …" />;
  }

  if (plan.isError) {
    return (
      <StateMessage
        tone="conflict"
        title="Schema konnte nicht geladen werden"
        action={<Button onClick={() => void plan.refetch()}>Erneut versuchen</Button>}
      >
        Ohne Schema bleibt die Prüfung bei der Werteliste. Der manuelle Weg ist offen.
      </StateMessage>
    );
  }

  return (
    <div className={styles.screen}>
      <header className={styles.head}>
        {/* No kicker here: the title itself carries the screen's one red
            mesh marker (Task 11, B-11). */}
        <h1 className={styles.title}>
          <span className="marke marke-titel" aria-hidden="true" />
          Rostlauf-Schema
        </h1>
        <p className={styles.lede}>
          Maßstabsgetreu aus den bestätigten Maßen gezeichnet. Stimmt die Form, stimmen die Maße —
          ein falsch gelesenes Maß bricht die Form sichtbar auf. Unbemaßte Bereiche bleiben grau.
        </p>
        {/* On both paths, because the step ends on both: Planung is a tab of
            a Vorgang (`app/caseTabs.ts`), and nobody looks up at the tabs for
            the next thing to do. */}
        <p className={styles.wayOn}>
          <Link className={styles.backToDraft} to="/planung">
            Weiter zur Planung
          </Link>
        </p>
      </header>

      <ul className={styles.legend}>
        <li>
          <span className={`${styles.swatch} ${styles.grating}`} aria-hidden="true" /> Abschnitt mit
          Rost
        </li>
        <li>
          <span className={`${styles.swatch} ${styles.plain}`} aria-hidden="true" /> ohne Rost
        </li>
        <li>
          <span className={`${styles.swatch} ${styles.dashed}`} aria-hidden="true" /> unbemaßt
        </li>
        <li>
          <span className={`${styles.swatch} ${styles.gap}`} aria-hidden="true" /> Klaffung
        </li>
      </ul>

      {plan.data.runs.map((run) => (
        <Panel
          key={run.id}
          title={run.label}
          hint={`Planstand ${plan.data.planVersion} · ${run.edges.length} Abschnitte`}
          aside={<StatusBadge status={run.closure.status}>{closureWord(run.closure)}</StatusBadge>}
          flush
        >
          <RunSchema run={run} />
          <ClosureReadout closure={run.closure} />
        </Panel>
      ))}

      <p className={styles.foot}>
        Das Schema ist in E1 eine Ansicht. Ziehen, Umschalten und Maße direkt im Bild ändern kommt
        mit dem Schema-Maus-Editor in E3.
      </p>
    </div>
  );
}

function closureWord(closure: ClosureCheck): string {
  if (!closure.evaluable) return "Nicht prüfbar";
  return closure.status === "conflict" ? "Schließt nicht" : "Schließt";
}
